import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ModalSearch } from './ModalSearch'
import { DATA_SEARCH_INIT, DataSearchType, acceptClassOptions, useModalScrollLock } from './utils'

type Props = object

const isMobileScreen = window.innerWidth < 768

const handleGetTheNearestMonth = (): string[] => {
  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth() + 1
  const result: string[] = []

  for (let i = 0; i < 3; i++) {
    const currentYear = year + Math.floor((month + i - 1) / 12)
    const currentMonth = ((month + i - 1) % 12) + 1
    result.push(`${currentYear}/${currentMonth.toString().padStart(2, '0')}`)
  }

  return result
}

export const SearchBar: React.FC<Props> = (_props: Props) => {
  const [openModal, setOpenModal] = useState<string>('')
  const [dataSearch, setDataSearch] = useState<DataSearchType>(DATA_SEARCH_INIT)
  const { t } = useTranslation()

  const handleOpenModal = (type: 'search' | 'filter') => {
    setOpenModal(type)
  }

  const handleCloseModal = () => {
    setOpenModal('')
  }

  const handleSetLocalStorageTopUrl = useCallback((queryString: string) => {
    if (queryString.length > 0) {
      localStorage.setItem('top_url', queryString)
    } else {
      localStorage.removeItem('top_url')
    }
  }, [])

  const handleClearLocalStorageTopUrl = () => {
    localStorage.removeItem('top_url')
  }

  useEffect(() => {
    const queryString = window.location.search
    handleSetLocalStorageTopUrl(queryString)
    if (open) {
      const queryParams = new URLSearchParams(queryString)
      const tempObject: DataSearchType = {
        maximum_number_of_adults: 0,
        maximum_number_of_children: 0,
        accept_class: [],
        no_car: '',
        stay_day: [],
        plan_month: [],
        text_search: '',
        category_name: [],
        number_of_preschoolers: 0,
        number_of_students: 0,
        number_of_preschoolers_1: 0,
        number_of_students_1: 0,
        number_of_study_abroad: 0,
        birthdays: [],
      }

      const arrayKeys = ['stay_day', 'plan_month', 'category_name']
      const booleanKeys = ['no_car']
      const numberKeys = ['maximum_number_of_adults', 'maximum_number_of_children']
      const keySearch = [
        'maximum_number_of_adults',
        'maximum_number_of_children',
        'accept_class',
        'no_car',
        'stay_day',
        'plan_month',
        'text_search',
        'category_name',
      ]

      queryParams.forEach((value, key) => {
        if (keySearch.includes(key)) {
          if (arrayKeys.includes(key)) {
            if (key === 'plan_month' && value === 'recently') {
              const nearestMonths = handleGetTheNearestMonth()
              tempObject[key] = nearestMonths
            } else {
              tempObject[key] = value ? value.split(',') : []
            }
          } else if (key === 'accept_class') {
            const dataArray = value ? value.split(',') : []
            tempObject[key] = dataArray.map((item, index) => ({
              id: index + 1,
              name: item,
              value: item,
            }))
          } else if (booleanKeys.includes(key)) {
            tempObject[key] = value
          } else if (numberKeys.includes(key)) {
            tempObject[key] = Number(value)
          } else {
            tempObject[key] = value
          }
        }
      })
      setDataSearch(tempObject)
    }
  }, [open, window, handleSetLocalStorageTopUrl])

  const handleGenerateSearchQueryURL = (currentKeySearch: string, data: DataSearchType) => {
    const query = {
      maximum_number_of_adults: String(data.maximum_number_of_adults),
      maximum_number_of_children: String(data.maximum_number_of_children),
      accept_class: data.accept_class?.map((item) => item.value).join(','),
      room_environment: String(data.no_car),
      stay_day: data.stay_day?.join(','),
      plan_month: data.plan_month?.join(','),
      text_search: data.text_search,
      category_name: data.category_name?.join(','),
    }
    const currentQueryParams = new URLSearchParams(currentKeySearch)
    Object.keys(query).forEach((key) => {
      if (query[key] && !['0', 'false'].includes(query[key])) {
        currentQueryParams.set(key, query[key])
      } else {
        currentQueryParams.delete(key)
      }
    })
    if (currentQueryParams.get('page')) {
      currentQueryParams.set('page', '1')
    }
    return currentQueryParams.toString()
  }

  const handleChangeDataSearch = (data: DataSearchType) => {
    setDataSearch(data)
    const currentKeySearch = window.location.search
    const queryURL = handleGenerateSearchQueryURL(currentKeySearch, data)
    window.location.href = queryURL ? `?${queryURL}` : '/'
  }

  useModalScrollLock(!!openModal)

  const findNameByValue = (arr: { value: string; name: string }[], value: string) =>
    arr.find((item) => item.value === value)?.name

  const renderAdultsAndChildren = () => {
    const { maximum_number_of_adults, maximum_number_of_children } = dataSearch
    const adults = maximum_number_of_adults > 0 ? `大${maximum_number_of_adults}` : ''
    const children = maximum_number_of_children > 0 ? `子${maximum_number_of_children}` : ''
    const separator = adults && children ? ':' : ''

    return `${adults}${separator}${children}`
  }

  const renderChildrenList = () => {
    const filteredChildren = Array.from(
      new Set(
        dataSearch.accept_class
          .filter((item) => item.value !== 'no_enrollment')
          .map((item) => findNameByValue(acceptClassOptions(t), item.value)),
      ),
    )
      .slice(0, 2)
      .join('.')

    return `${filteredChildren}${
      dataSearch?.accept_class?.filter((item) => item.value !== 'no_enrollment')?.length > 0 && dataSearch.accept_class.length > 2
        ? '...'
        : ''
    }`
  }

  const renderPlanMonth = () => {
    const sortedPlanMonth = dataSearch.plan_month.sort((a, b) => {
      const [yearA, monthA] = a.split('/')
      const [yearB, monthB] = b.split('/')
      if (yearA === yearB) {
        return parseInt(monthA) - parseInt(monthB)
      } else {
        return parseInt(yearA) - parseInt(yearB)
      }
    })
    return sortedPlanMonth.join('.')
  }

  const renderSearchBarContent = () => {
    const isDataSearchEmpty = () => {
      return (
        dataSearch.maximum_number_of_adults === 0 &&
        dataSearch.maximum_number_of_children === 0 &&
        dataSearch.accept_class.length === 0 &&
        dataSearch.no_car === '' &&
        dataSearch.stay_day.length === 0 &&
        dataSearch.plan_month.length === 0 &&
        dataSearch.text_search === '' &&
        dataSearch.category_name.length === 0
      )
    }
    const hasAdultsOrChildren = dataSearch.maximum_number_of_adults > 0 || dataSearch.maximum_number_of_children > 0
    const hasEnrolledChildren = dataSearch.accept_class?.filter((item) => item.value !== 'no_enrollment')?.length > 0
    const hasPlanMonth = dataSearch.plan_month?.filter((item) => item)?.length > 0

    return (
      <div
        className="search-bar-content justify-start"
        style={{ marginRight: '5px', gap: '5px', width: isMobileScreen ? '87%' : '89%' }}
      >
        {isDataSearchEmpty() && (
          <div className="font-size-13 overflow-hidden whitespace-nowrap text-left text-ellipsis">
            {t('top.modal_search.default_placeholder')}
          </div>
        )}
        {hasAdultsOrChildren && (
          <div className="flex items-center font-size-13" style={{ width: 'auto', maxWidth: '20%' }}>
            <div className="whitespace-nowrap text-left">{renderAdultsAndChildren()}</div>
            {(hasEnrolledChildren || hasPlanMonth) && <span className="ml-2 text-gray-400">|</span>}
          </div>
        )}
        {hasEnrolledChildren && (
          <div className="flex items-center font-size-13 w-fit" style={{ maxWidth: hasPlanMonth ? '40%' : '80%' }}>
            <div className="overflow-hidden whitespace-nowrap text-left text-ellipsis">{renderChildrenList()}</div>
            {hasPlanMonth && <span className="ml-1 text-gray-400">|</span>}
          </div>
        )}
        {hasPlanMonth && (
          <div
            className="font-size-13 overflow-hidden whitespace-nowrap text-left text-ellipsis"
            style={{ width: hasEnrolledChildren ? '40%' : '60%' }}
          >
            {renderPlanMonth()}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div
        className={`search-bar-wrapper w-full flex items-center justify-center block`}
        style={{
          padding: '0px 5px',
          width: isMobileScreen ? '95%' : '390px',
        }}
      >
        <button
          onClick={() => handleOpenModal('search')}
          className="all-unset border flex items-center button-search-bar"
          style={{ marginRight: '5px' }}
        >
          {renderSearchBarContent()}
          <div style={{ width: isMobileScreen ? '13%' : '10%' }}>
            <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16.5" cy="16" r="16" fill="#3BB788" />
              <circle cx="15.5" cy="15" r="4" stroke="white" strokeWidth="2" />
              <line x1="21.7929" y1="21.7071" x2="17.5503" y2="17.4645" stroke="white" strokeWidth="2" />
            </svg>
          </div>
        </button>
      </div>
      {openModal === 'search' && (
        <ModalSearch
          initDataSearch={dataSearch}
          onChangeDataSearch={handleChangeDataSearch}
          open={openModal === 'search'}
          onClose={handleCloseModal}
          handleClearTopUrl={handleClearLocalStorageTopUrl}
        />
      )}
    </>
  )
}
